import { Box, Typography } from "@mui/material";
import { DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import { copyIcon } from "@common/icons.tsx";
import React from "react";
import QuoteBlock from "./QuoteBlock";
import { BestPracticeCheckFindingType, BestPracticeCheckFindingViewModel } from "@common/components/bestPracticeCheckDialog/BestPracticeCheckDialog.types.ts";
import { useTranslation } from "../../../localization/useTranslation.ts";

interface FindingCardProps {
    finding: BestPracticeCheckFindingViewModel;
    getFindingTypeColor: (findingType: BestPracticeCheckFindingType) => string;
    getFindingTypeIcon: (findingType: BestPracticeCheckFindingType) => React.ReactNode;
    copyToClipboard: (text: string) => void;
}

function FindingCard(props: FindingCardProps) {
    const { finding, getFindingTypeColor, getFindingTypeIcon, copyToClipboard } = props;
    const { translateString } = useTranslation();
    const findingTypeColor = getFindingTypeColor(finding.findingType);

    return (
        <Box
            sx={(theme) => ({
                borderLeft: `4px solid ${findingTypeColor}`,
                border: `1px solid ${theme.palette.grey["200"]}`,
                borderLeftWidth: 4,
                borderLeftColor: findingTypeColor,
                borderRadius: "0 8px 8px 0",
                padding: 2,
                "&:hover": {
                    backgroundColor: theme.palette.grey["50"],
                },
            })}
        >
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", marginBottom: 1 }}>
                {getFindingTypeIcon(finding.findingType)}
                <DotLegalHeader headerStyle="small" marginBottom={0}>
                    {finding.title}
                </DotLegalHeader>
            </Box>

            <Typography variant="body2" sx={{ marginBottom: 1 }}>
                {finding.description}
            </Typography>

            {finding.documentQuote && <QuoteBlock label={translateString("documentQuote")} text={finding.documentQuote} />}

            {finding.referenceQuote && <QuoteBlock label={translateString("referenceQuote")} text={finding.referenceQuote} />}

            {finding.recommendation && (
                <Box sx={{ backgroundColor: "#F8FBFF", borderRadius: 1, padding: 1.5, marginTop: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <Box>
                            <DotLegalHeader headerStyle="small" marginBottom={0}>
                                {translateString("recommendation")}
                            </DotLegalHeader>
                            <Typography variant="body2">{finding.recommendation}</Typography>

                            <Box
                                sx={(theme) => ({
                                    display: "flex",
                                    gap: 1,
                                    mt: 1,
                                    alignItems: "center",
                                    "& svg": { stroke: theme.palette.primary.main },
                                })}
                            >
                                {copyIcon}

                                <Typography
                                    sx={(theme) => ({
                                        color: theme.palette.primary.main,
                                        fontSize: theme.typography.pxToRem(12),
                                        "&:hover": { cursor: "pointer", textDecoration: "underline" },
                                    })}
                                    onClick={() => copyToClipboard(finding.recommendation!)}
                                >
                                    {translateString("copyText")}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
}

export default FindingCard;
